import {useQuery} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import {useMemo} from 'react';
import {uniq} from 'underscore';

import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {maybeGetTypenameFromID} from 'graphql-client/getTypenameFromID';
import {
  ParentStorableMinimalCapacityQuery,
  ParentStorableMinimalCapacityQueryVariables,
} from 'inventory/location-validation/__generated/ParentStorableMinimalCapacityQuery';
import {
  isValidParentStorableWithCapacityInfo,
  PARENT_STORABLE_MINIMAL_CAPACITY_QUERY,
  ParentStorableWithCapacityInfo,
} from 'inventory/location-validation/ParentStorableValidationUtils';
import sortedBy from 'util/sortedBy';

/** Loaded capacity by parent storable id. Parents that are loading, failed to load, or cannot hold children are absent. */
export interface ParentStorableCapacityById {
  [parentStorableId: string]: ParentStorableWithCapacityInfo;
}

const NO_CAPACITIES: ParentStorableCapacityById = {};

/**
 * Load the capacity of the given parent storables.
 *
 * Reads through the Apollo cache, which chip queries and the row updaters' capacity refresh keep populated, and
 * fetches whatever is missing. Partial results return cached parents while other parents load.
 */
export default function useParentStorableCapacities(
  parentStorableIds: Array<string>
): ParentStorableCapacityById {
  const storableIdsKey = sortedBy(uniq(parentStorableIds), [(id) => id]).join(',');
  const storableIds = useMemo(() => (storableIdsKey ? storableIdsKey.split(',') : []), [storableIdsKey]);

  const {data, error} = useQuery<
    ParentStorableMinimalCapacityQuery,
    ParentStorableMinimalCapacityQueryVariables
  >(PARENT_STORABLE_MINIMAL_CAPACITY_QUERY, {
    variables: {storableIds},
    fetchPolicy: 'cache-first',
    returnPartialData: true,
    skip: storableIds.length === 0,
  });

  // Failed fetches are left to the backend, which reports capacity errors on submit. Partial results omit fields
  // of parents that are still loading, including the name of parents whose capacity the row updaters cached.
  const storables = error == null ? data?.storables : undefined;
  return useMemo(
    () =>
      storables == null
        ? NO_CAPACITIES
        : objectFromEntries(
            storables.flatMap((storable) =>
              isValidParentStorableWithCapacityInfo(storable) &&
              storable.name !== undefined &&
              storable.totalCapacity !== undefined &&
              storable.occupiedCapacity !== undefined
                ? [[storable.id, storable] as [string, ParentStorableWithCapacityInfo]]
                : []
            )
          ),
    [storables]
  );
}

/**
 * Drop the cached occupied capacity of the given parent storables, so that `useParentStorableCapacities` refetches it.
 */
export function clearCachedParentStorableCapacities(parentStorableIds: Array<string>): void {
  const {cache} = getApolloClient();
  for (const id of uniq(parentStorableIds)) {
    const typename = maybeGetTypenameFromID(getPrefixToTypename(), id);
    if (typename != null) {
      cache.evict({id: `${typename}:${id}`, fieldName: 'occupiedCapacity'});
    }
  }
}
